import { useState, useEffect, useCallback, useMemo } from "react";
import * as tasksService from "../services/tasksService";

/**
 * Hook principal del módulo de tareas.
 *
 * Soporta dos vistas:
 *   - "my":  GET /tasks/my-tasks?employeeId=X (incluye myRole)
 *   - "all": GET /tasks (admin/manager)
 *
 * Mantiene tasks + stats sincronizados y expone CRUD completo.
 *
 * @param {Object} args
 * @param {"my"|"all"} args.view
 * @param {number} args.employeeId - Requerido si view = "my"
 * @param {Object} [args.filters] - status, priorityId, categoryId, locationId
 *                                  + (en view "all") assigneeId, creatorId, cipId, relatedType
 */
// Normaliza employeeId a número entero. Si el JWT/storage lo entrega como
// string ("89") o undefined, aquí se convierte a 89 o null. El backend
// espera int en /my-tasks y al crear, así que tipamos consistentemente.
const toEmployeeIdOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export default function useTasksModule({ view = "my", employeeId, filters = {} }) {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Versión normalizada del id — siempre número o null.
  const safeEmployeeId = useMemo(() => toEmployeeIdOrNull(employeeId), [employeeId]);

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  const refresh = useCallback(async () => {
    if (view === "my" && !safeEmployeeId) {
      console.warn(
        "[useTasksModule] No se cargaron tareas: employeeId inválido o vacío",
        { received: employeeId, normalized: safeEmployeeId }
      );
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const statsFilters = {
        locationId: filters.locationId,
        ...(view === "my" ? { assigneeId: safeEmployeeId } : {}),
      };
      const [tasksData, statsData] = await Promise.all([
        view === "my"
          ? tasksService.getMyTasks(safeEmployeeId, filters)
          : tasksService.getTasks(filters),
        tasksService.getTaskStats(statsFilters),
      ]);
      setTasks(tasksData);
      setStats(statsData);
    } catch (err) {
      console.error("[useTasksModule] refresh error:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
    // filtersKey memoiza el JSON.stringify(filters) para evitar refetch en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, safeEmployeeId, filtersKey]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createTask = useCallback(
    async (taskData) => {
      const result = await tasksService.createTask({
        ...taskData,
        creatorId: toEmployeeIdOrNull(taskData.creatorId) ?? safeEmployeeId,
      });
      await refresh();
      return result;
    },
    [safeEmployeeId, refresh]
  );

  const updateTask = useCallback(
    async (taskId, taskData) => {
      const result = await tasksService.updateTask(taskId, {
        ...taskData,
        employeeId: toEmployeeIdOrNull(taskData.employeeId) ?? safeEmployeeId,
      });
      await refresh();
      return result;
    },
    [safeEmployeeId, refresh]
  );

  const completeTask = useCallback(
    async (taskId) => {
      const result = await tasksService.completeTask(taskId, safeEmployeeId);
      await refresh();
      return result;
    },
    [safeEmployeeId, refresh]
  );

  const updateStatus = useCallback(
    async (taskId, newStatus) => {
      const result = await tasksService.updateTaskStatus(taskId, newStatus, safeEmployeeId);
      await refresh();
      return result;
    },
    [safeEmployeeId, refresh]
  );

  const deleteTask = useCallback(
    async (taskId) => {
      const result = await tasksService.deleteTask(taskId, safeEmployeeId);
      await refresh();
      return result;
    },
    [safeEmployeeId, refresh]
  );

  const getTaskById = useCallback(
    (taskId) => tasksService.getTaskById(taskId),
    []
  );

  return {
    tasks,
    stats,
    loading,
    error,
    refresh,
    createTask,
    updateTask,
    completeTask,
    updateStatus,
    deleteTask,
    getTaskById,
  };
}
